<template>
  <div>
    <div class="flex space-x-4 items-center">
      <div
        class="rounded-xs flex justify-center items-center align-middle h-14 w-14 bg-gray-50"
        v-if="activeIcon && activeColor"
      >
        <span
          class="material-symbols-outlined text-3xl"
          :style="{ color: activeColor }"
          >{{ activeIcon }}</span
        >
      </div>
      <div>
        <button
          @click="isIconSelectorOpen = true"
          class="rounded-md border border-gray-300 bg-white px-2.5 py-2 text-base font-medium text-gray-700 shadow-sm focus:outline-none sm:w-auto sm:text-sm disabled:opacity-50"
          :class="{
            '!cursor-not-allowed': disabled,
            'hover:text-gray-500': !disabled,
          }"
          :disabled="disabled"
        >
          {{
            activeIcon && activeColor
              ? t("admin.settings.categories.category_icon.edit")
              : t("admin.settings.categories.category_icon.select")
          }}
        </button>
      </div>
    </div>

    <TransitionRoot as="template" :show="!!isIconSelectorOpen">
      <Dialog as="div" class="relative z-backdrop-reset" @close="close">
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-200"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div
            class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          />
        </TransitionChild>

        <div class="fixed inset-0 overflow-y-auto">
          <div
            class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0"
          >
            <TransitionChild
              as="template"
              enter="ease-out duration-300"
              enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enter-to="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leave-from="opacity-100 translate-y-0 sm:scale-100"
              leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel
                class="relative transform overflow-hidden rounded-md bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6"
              >
                <div class="flex items-center justify-between">
                  <DialogTitle
                    as="h3"
                    class="text-lg font-medium leading-6 text-gray-900"
                    >Select icon</DialogTitle
                  >
                  <button
                    type="button"
                    class="rounded-sm bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                    @click="close"
                  >
                    <span class="sr-only">Close</span>
                    <XMarkIcon class="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <div class="flex flex-col space-y-4 mt-4">
                  <colors
                    :active="activeColor"
                    @color-selected="handleActiveColorSelected"
                  />
                  <icons
                    :color="activeColor"
                    @icon-selected="handleIconSelected"
                  />
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
  </div>
</template>

<script setup>
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from "@headlessui/vue";
import { XMarkIcon } from "@heroicons/vue/24/outline";
import { defineEmits, defineProps, ref } from "vue";
import Colors from "@/Tailwind/Components/Settings/Categories/Colors";
import Icons from "@/Tailwind/Components/Settings/Categories/Icons";
import { useI18n } from "vue-i18n";

const emit = defineEmits(["change"]);
const props = defineProps({
  value: Object,
  defaultColor: String,
  disabled: Boolean,
});

const { t } = useI18n();

const isIconSelectorOpen = ref(false);
const activeColor = ref(props.value?.color ?? props.defaultColor);
const activeIcon = ref(props.value?.icon);

const handleIconSelected = (icon) => {
  activeIcon.value = icon;
  emit("change", { icon: activeIcon.value, color: activeColor.value });

  isIconSelectorOpen.value = false;
};

const handleActiveColorSelected = (color) => {
  activeColor.value = color;
  emit("change", { icon: activeIcon.value, color: activeColor.value });
};

const close = () => (isIconSelectorOpen.value = false);
</script>
